import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from "@mui/material";
import type { RiskAssessment } from "../../services/generateRiskAssessmentDoc";

/**
 * Read-only preview of a generated risk assessment — shared by the Generator
 * (right-hand panel) and the Library (preview dialog) so there's one source of truth.
 * Severity/likelihood/risk are intentionally not shown here — the assessor completes
 * them in the Word document.
 */
const AssessmentPreview = ({ assessment }: { assessment: RiskAssessment }) => {
    const hazards = assessment.hazards || [];

    return (
        <Box>
            <Typography variant="h6">{assessment.title}</Typography>
            {assessment.activity ? (
                <Typography variant="body2" style={{ color: "#666" }}>
                    {assessment.activity}
                </Typography>
            ) : null}
            <Typography
                variant="caption"
                style={{ color: "#999", display: "block", margin: "6px 0 12px" }}
            >
                {hazards.length} hazard{hazards.length === 1 ? "" : "s"} · severity, likelihood and
                risk are completed by the assessor in the Word document
            </Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell style={{ fontWeight: 600, width: 28 }}>#</TableCell>
                        <TableCell style={{ fontWeight: 600 }}>Hazard</TableCell>
                        <TableCell style={{ fontWeight: 600 }}>People at risk</TableCell>
                        <TableCell style={{ fontWeight: 600 }}>Controls in place</TableCell>
                        <TableCell style={{ fontWeight: 600 }}>Additional controls</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {hazards.map((h, i) => (
                        <TableRow key={i}>
                            <TableCell style={{ verticalAlign: "top", color: "#999" }}>{i + 1}</TableCell>
                            <TableCell style={{ verticalAlign: "top" }}>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {h.name}
                                </Typography>
                                {h.description ? (
                                    <Typography variant="caption" style={{ color: "#777" }}>
                                        {h.description}
                                    </Typography>
                                ) : null}
                            </TableCell>
                            <TableCell style={{ verticalAlign: "top", color: "#555" }}>
                                {(h.peopleAtRisk || []).join(", ")}
                            </TableCell>
                            <TableCell style={{ verticalAlign: "top" }}>
                                {(h.controlsInPlace || []).length > 0 ? (
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                        {(h.controlsInPlace || []).map((c, ci) => (
                                            <li key={ci}>
                                                <Typography variant="caption" style={{ color: "#2e7d32" }}>{c}</Typography>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <Typography variant="caption" style={{ color: "#999" }}>—</Typography>
                                )}
                            </TableCell>
                            <TableCell style={{ verticalAlign: "top" }}>
                                {(h.additionalControls || []).length > 0 ? (
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                        {(h.additionalControls || []).map((c, ci) => (
                                            <li key={ci}>
                                                <Typography variant="caption" style={{ color: "#ad6800" }}>{c}</Typography>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <Typography variant="caption" style={{ color: "#999" }}>—</Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

export default AssessmentPreview;
